  <template>
    <div class="container-fluid py-4">
      <div class="row justify-content-center">
        <div class="col-lg-10 col-md-12">
          <div class="card">
            <div class="card-body p-4">
              <!-- Loading State -->
              <div v-if="loading" class="text-center py-4">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mt-2">Loading swap details...</p>
              </div>

              <!-- Content -->
              <template v-else>
                <!-- Swap Details Box (only when there is any content from API) -->
                <div class="border border-dark p-4 mb-4 text-sm rounded"
                  v-if="displaySwapDetails && (displaySwapDetails.location || displaySwapDetails.email || displaySwapDetails.swapDescription)">
                  <div class="mb-2">
                    <strong>LOCATION:</strong> {{ displaySwapDetails.location }}
                  </div>
                  <div class="mb-2">
                    <strong>YOUR EMAIL:</strong> {{ displaySwapDetails.email }}
                  </div>
                  <div class="mb-0">
                    <strong>SWAP DESCRIPTION:</strong> {{ displaySwapDetails.swapDescription }}
                  </div>
                </div>

              <!-- Unavailable Notice -->
              <div v-if="unavailable" class="alert alert-warning d-flex align-items-center gap-2 mb-4">
                <i class="material-icons">info</i>
                <span>{{ unavailableMessage }}</span>
              </div>

              <!-- User Message -->
              <div class="mb-4">
                <p class="mb-2">Hello <strong>{{ displayUserName }}</strong>,</p>
                <p class="mb-2 text-success fw-bold" v-if="!unavailable">{{ displayMessage }}</p>
                <p class="mb-2" v-if="displayAdditionalMessage">{{ displayAdditionalMessage }}</p>
                <p class="mb-0">Thank you.</p>
              </div>


                <!-- Divider -->
                <hr class="my-4" />

                <!-- Did You Know Section (only when API provides content) -->
                <div class="mb-4"
                  v-if="showDidYouKnow && (displayDidYouKnowText || (displayDidYouKnowSteps && displayDidYouKnowSteps.length > 0))">
                  <h5 class="fw-bold mb-3">Did You Know?</h5>
                  <p class="mb-3" v-if="displayDidYouKnowText">{{ displayDidYouKnowText }}</p>
                  <p class="mb-2" v-if="displayDidYouKnowSteps && displayDidYouKnowSteps.length > 0">To setup an
                    external calendar, follow the steps below:</p>
                  <ul class="mb-3" v-if="displayDidYouKnowSteps && displayDidYouKnowSteps.length > 0">
                    <li v-for="(step, index) in displayDidYouKnowSteps" :key="index" class="mb-2">{{ step }}</li>
                  </ul>
                </div>

                <!-- Login Link -->
                <router-link to="/sign-in">
                  <MaterialButton color="success">
                    Click here to Login
                  </MaterialButton>
                </router-link>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from 'vue-toast-notification'
import MaterialButton from '@/components/common/MaterialButton.vue'
import api from '@/config/api'

const route = useRoute()
const toast = useToast()

// State
const loading = ref(true)
const swapDetails = ref({
  location: '',
  email: '',
  swapDescription: ''
})
const userName = ref('')
const message = ref('')
const additionalMessage = ref('')
const unavailable = ref(false)
const unavailableMessage = ref('')
const showDidYouKnow = ref(true)
const didYouKnowText = ref('')
const didYouKnowSteps = ref([])

// Display values from API only (empty string when no data)
const displaySwapDetails = computed(() => swapDetails.value)

const displayUserName = computed(() => userName.value || '')

const displayMessage = computed(() => message.value || '')

const displayAdditionalMessage = computed(() => additionalMessage.value || '')

const displayDidYouKnowText = computed(() => didYouKnowText.value || '')

const displayDidYouKnowSteps = computed(() => didYouKnowSteps.value || [])

// Confirm swap response (call on page load, show result in toast)
const confirmSwapEmailResponse = async () => {
  const encodedString = route.params.encodedString
  if (!encodedString) return

  try {
    const response = await api.get(`/account/swapemailresponse/cs/${encodedString}`)
    if (response.data?.unavailable) {
      unavailable.value = true
      unavailableMessage.value = response.data.message || 'This swap request is no longer available.'
      return
    }
    toast.info(response.data.result?.message || response.data.message, { position: 'top-right' })
  } catch (error) {
    console.error('Error confirming swap response:', error)
    const errData = error.response?.data
    if (errData?.unavailable) {
      unavailable.value = true
      unavailableMessage.value = errData.message || 'This swap request is no longer available.'
      return
    }
    const errMsg = errData?.message || error.message || 'Failed to confirm swap response.'
    toast.error(errMsg, { position: 'top-right' })
  }
}

// Fetch swap email response details
const fetchSwapEmailResponseDetails = async () => {
  const encodedString = route.params.encodedString
  if (!encodedString) {
    console.error('No encodedString found in route params')
    loading.value = false
    return
  }

  try {
    loading.value = true
    const response = await api.get(`/account/swapemailresponse-details/cs/${encodedString}`)

    if (response.data) {
      const data = response.data

      // Map API response to component state
      if (data.assignmentDetails) {
        swapDetails.value = {
          location: data.assignmentDetails.location || '',
          email: data.assignmentDetails.email || '',
          swapDescription: data.assignmentDetails.swap_description || ''
        }
      }

      userName.value = data.username || ''
      message.value = data.message || ''
      additionalMessage.value = data.additionalMessage || ''
      if (data.unavailable) {
        unavailable.value = true
        unavailableMessage.value = data.message || 'This swap request is no longer available.'
      }
      if (data.didYouKnowText != null) didYouKnowText.value = data.didYouKnowText
      if (data.didYouKnowSteps && Array.isArray(data.didYouKnowSteps)) didYouKnowSteps.value = data.didYouKnowSteps
    }
  } catch (error) {
    console.error('Error fetching swap email response details:', error)
  } finally {
    loading.value = false
  }
}

// On mount: confirm swap (show toast), then fetch details
onMounted(async () => {
  await confirmSwapEmailResponse()
  await fetchSwapEmailResponseDetails()
})
</script>

